import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonRow,
} from '@ionic/react';
import { pencilOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { collection, getCountFromServer, getFirestore, query, where } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import Content from '../components/Content';
import { useConfig } from '../data/store/useConfig';
import { useRoles } from '../data/store/useRoles';
import { useAdminPrefs } from '../data/store/useAdminPrefs';
import { defaultConfiguration } from '../models/Configuration';
import { usePageTitle } from '../util/sugar';
import { APP_TITLE, IS_SADFAM, IS_SCHOOL_OF_SONG, SUPPORT_EMAIL } from '../config/env';
import './Mission.scss';

// SADFAM-specific copy — this page previously hardcoded this content for
// every instance. Only show it on the SADFAM instance itself; every other
// instance falls back to its own configuration/public doc, or a brand-
// neutral default below.
const SADFAM_HEADING = 'Woody Guthrie and Bob Marley woke up early and wrote songs. We challenge you to do the same — every day.';
const SADFAM_BODY = 'No song has to be a masterpiece — that\'s the point. When you write thirty in a row, each one weighs less, and you can finally try the idea you\'d never risk on something precious.\n\nA "song" is whatever you decide it is: a field recording, a kitchen banjo tune, a krautrock experiment, a round for your friends. What holds it together isn\'t genre — it\'s the daily practice and the people doing it alongside you, trading honest feedback until the whole archive starts to rhyme with itself.';
const DEFAULT_HEADING = IS_SADFAM ? SADFAM_HEADING : `Welcome to ${APP_TITLE}.`;
const DEFAULT_BODY = IS_SADFAM ? SADFAM_BODY : '';

const Mission: React.FC = () => {
  const { config, changeConfig } = useConfig();
  const { role } = useRoles();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [songCount, setSongCount] = useState<number | null>(null);
  const [artistCount, setArtistCount] = useState<number | null>(null);

  const missionHeading = config?.mission?.trim() || DEFAULT_HEADING;
  const missionBody = config?.rules?.trim() || DEFAULT_BODY;
  const configId = config?.id || defaultConfiguration.id;

  const isAdmin = role?.authority?.some(a => a === 'admin' || a === 'super admin') ?? false;
  const { missionEditMode } = useAdminPrefs();

  usePageTitle('Mission');

  useEffect(() => {
    // School of Song doesn't show the song/participant stats sentence —
    // skip the counts entirely rather than fetch data nothing renders.
    if (IS_SCHOOL_OF_SONG) return;
    try {
      const db = getFirestore(getApp());
      // Count only public songs: aggregation queries obey the security rules
      // like list queries, and an unfiltered count over `songs` is denied for
      // non-staff now that privacy tiers are enforced server-side.
      getCountFromServer(query(collection(db, 'songs'), where('privacy', '==', 'public'))).then(s => setSongCount(s.data().count)).catch(() => {});
      // Same for artists: a profile without an explicit public tier is not
      // world-readable (S02/S06 rules), so count the public ones.
      getCountFromServer(query(collection(db, 'artists'), where('privacy', '==', 'public'), where('songs', '>', 0))).then(s => setArtistCount(s.data().count)).catch(() => {});
    } catch (_) {}
  }, []);

  const startEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const saveEdit = () => {
    const value = textareaRef.current?.value ?? editValue;
    if (editingField) changeConfig(configId, editingField, value);
    setEditingField(null);
  };

  return (
    <Content id="mission-page">
      <IonGrid>
        <IonRow>
          <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">

            {/* Mission — all three sections in one card */}
            <IonCard>
              <IonCardContent>

                <h1 className="mission-title">
                  {IS_SADFAM
                    ? 'Welcome to Song A Day For A Month (SADFAM)'
                    : config?.title || defaultConfiguration.title}
                </h1>

                <div className="mission-body-divider" />

                {/* 1. Woody Guthrie heading */}
                {editingField === 'mission' ? (
                  <>
                    <textarea
                      key="mission"
                      ref={textareaRef}
                      defaultValue={editValue}
                      rows={4}
                      autoFocus
                      className="mission-textarea-native"
                    />
                    <div className="mission-edit-actions">
                      <IonButton size="small" onClick={saveEdit}>Save</IonButton>
                      <IonButton size="small" fill="outline" onClick={() => setEditingField(null)}>Cancel</IonButton>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="mission-heading">{missionHeading}</p>
                    {isAdmin && missionEditMode && (
                      <IonButton fill="clear" size="small" onClick={() => startEdit('mission', missionHeading)}>
                        <IonIcon slot="start" icon={pencilOutline} />
                        Edit heading
                      </IonButton>
                    )}
                  </>
                )}

                <div className="mission-body-divider" />

                {/* 2. Live stats — School of Song doesn't want these shown */}
                {!IS_SCHOOL_OF_SONG && (
                  <>
                    <p className="mission-counter-sentence">
                      <span className="mission-count">{artistCount !== null ? artistCount.toLocaleString() : '———'}</span> musicians have collectively written{' '}
                      <span className="mission-count">{songCount !== null ? songCount.toLocaleString() : '———'}</span> songs
                      {IS_SADFAM ? ' since 2014 on SADFAM.' : ` on ${config?.title || defaultConfiguration.title}.`}
                    </p>

                    <div className="mission-body-divider" />
                  </>
                )}

                {/* 3. Artists own their own songs */}
                {editingField === 'rules' ? (
                  <>
                    <textarea
                      key="rules"
                      ref={textareaRef}
                      defaultValue={editValue}
                      rows={10}
                      autoFocus
                      className="mission-textarea-native"
                    />
                    <div className="mission-edit-actions">
                      <IonButton size="small" onClick={saveEdit}>Save</IonButton>
                      <IonButton size="small" fill="outline" onClick={() => setEditingField(null)}>Cancel</IonButton>
                    </div>
                  </>
                ) : (
                  <>
                    {missionBody.split('\n\n').map((para, i) => (
                      <p key={i} className="mission-body-para">{para}</p>
                    ))}
                    {isAdmin && missionEditMode && (
                      <IonButton fill="clear" size="small" onClick={() => startEdit('rules', missionBody)}>
                        <IonIcon slot="start" icon={pencilOutline} />
                        Edit text
                      </IonButton>
                    )}
                  </>
                )}

              </IonCardContent>
            </IonCard>

            {/* Links — School of Song doesn't want the "Ask us a question"
                mailto link, and (unlike SADFAM) has nothing else in this
                card, so skip rendering the card at all for that instance. */}
            {!IS_SCHOOL_OF_SONG && (
              <IonCard>
                {IS_SADFAM ? (
                  <>
                    <IonItem lines="full" routerLink="/support" detail>
                      <IonLabel>Support us with a donation</IonLabel>
                    </IonItem>
                    <IonItem lines="full" href="mailto:songadayforamonth@gmail.com" detail>
                      <IonLabel>Ask us a question</IonLabel>
                    </IonItem>
                    <IonItem lines="full" href="https://sadfamrecords.bandcamp.com" target="_blank" rel="noopener noreferrer" detail>
                      <IonLabel>Follow us on Bandcamp</IonLabel>
                    </IonItem>
                    <IonItem lines="none" href="https://sadfamrecords.substack.com/" target="_blank" rel="noopener noreferrer" detail>
                      <IonLabel>Follow us on Substack</IonLabel>
                    </IonItem>
                  </>
                ) : (
                  <IonItem lines="none" href={`mailto:${config?.supportEmail || SUPPORT_EMAIL}`} detail>
                    <IonLabel>Ask us a question</IonLabel>
                  </IonItem>
                )}
              </IonCard>
            )}

          </IonCol>
        </IonRow>
      </IonGrid>
    </Content>
  );
};

export default Mission;
